import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Booking,
  CreateBookingInput,
  UpdateBookingInput,
} from '@/types/Booking';
import { Service } from '@/types/Service';
import { Contact } from '@/types/Contact';
import { format, parseISO } from 'date-fns';
import { useToast } from '@/hooks/useToast';
import {
  contactsApi,
  type BusinessHour,
  type AvailabilityOverride,
} from '@/services/api';
import TimeInput24h from '@/components/page-components/project/agents/ui/TimeInput24h';
import { getEffectiveHoursForDate } from '@/utils/availability';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { Calendar as CalendarIcon } from 'lucide-react';
import { useDateLocale } from '@/hooks/useDateLocale';

interface AppointmentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (booking: CreateBookingInput | UpdateBookingInput) => Promise<void>;
  booking?: Booking | null;
  services: Service[];
  contacts: Contact[];
  agentId: number;
  initialDate?: Date | null;
  initialHour?: number | null;
  businessHours?: BusinessHour[];
  availabilityOverrides?: AvailabilityOverride[];
  onContactCreated?: (contact: Contact) => void;
}

export const AppointmentDialog: React.FC<AppointmentDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  booking,
  services,
  contacts,
  agentId,
  initialDate,
  initialHour,
  businessHours = [],
  availabilityOverrides = [],
  onContactCreated,
}) => {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const parseTimestamp = (value: string | null | undefined): Date | null => {
    if (!value) return null;
    const isoParsed = parseISO(value);
    if (!isNaN(isoParsed.getTime())) {
      return isoParsed;
    }
    const nativeParsed = new Date(value);
    if (!isNaN(nativeParsed.getTime())) {
      return nativeParsed;
    }
    return null;
  };

  const isValidPhoneNumber = (value: string): boolean => {
    const trimmed = value.trim();
    if (!trimmed) return false;

    // Allow common phone formatting characters, while blocking letters/symbols.
    if (!/^\+?[0-9\s\-().]+$/.test(trimmed)) return false;

    // Validate sensible length based on digits only.
    const digitsOnly = trimmed.replace(/\D/g, '');
    return digitsOnly.length >= 7 && digitsOnly.length <= 15;
  };

  // Initialize with current date/time as defaults (end date same as start for same-day appointments)
  const getDefaultDateTime = () => {
    const now = new Date();
    const endDefault = new Date(now.getTime() + 60 * 60 * 1000);
    return {
      start: format(now, 'yyyy-MM-dd'),
      startTime: format(now, 'HH:mm'),
      endTime: format(endDefault, 'HH:mm'),
    };
  };

  const defaults = getDefaultDateTime();

  const [serviceId, setServiceId] = useState<string>('');
  const [contactId, setContactId] = useState<string>('');
  const [newContactPhone, setNewContactPhone] = useState<string>('');
  const [newContactName, setNewContactName] = useState<string>('');
  const [useNewContact, setUseNewContact] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<string>(defaults.start);
  const [startTime, setStartTime] = useState<string>(defaults.startTime);
  const [endTime, setEndTime] = useState<string>(defaults.endTime);
  const [status, setStatus] = useState<string>('confirmed');
  const [customerNotes, setCustomerNotes] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [showOutsideHoursConfirm, setShowOutsideHoursConfirm] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const isInitialLoad = useRef(true);
  const selectedStartDate = startDate ? parseISO(startDate) : undefined;
  const hasInvalidNewContactPhone =
    useNewContact &&
    newContactPhone.trim().length > 0 &&
    !isValidPhoneNumber(newContactPhone);
  const selectedService = services.find(s => s.id.toString() === serviceId);
  const isSelectedServiceDisabled = selectedService?.enabled === false;

  const effectiveHours = getEffectiveHoursForDate(
    startDate,
    businessHours,
    availabilityOverrides
  );
  const isDayClosed = effectiveHours?.is_closed === true;
  const dayOpenTime =
    effectiveHours && !effectiveHours.is_closed
      ? effectiveHours.start_time
      : null;
  const dayCloseTime =
    effectiveHours && !effectiveHours.is_closed
      ? effectiveHours.end_time
      : null;

  useLayoutEffect(() => {
    if (!isOpen) {
      // Don't update state when dialog is closed
      return;
    }

    isInitialLoad.current = true;

    if (booking) {
      // Editing existing booking
      const start = parseTimestamp(booking.start_time);
      const end = parseTimestamp(booking.end_time);
      if (!start || !end) {
        toast({
          variant: 'destructive',
          title: t(
            'agent.calendar.appointment.errors.invalid_time.translation',
            'Invalid Time'
          ),
          description: t(
            'agent.calendar.appointment.errors.invalid_date_format.translation',
            'Please provide valid date and time values.'
          ),
        });
      } else {
        setServiceId(booking.service_id?.toString() || '');
        setContactId(booking.contact_id?.toString() || '');
        const normalizedStartDate = format(start, 'yyyy-MM-dd');
        setStartDate(normalizedStartDate);
        setStartTime(format(start, 'HH:mm'));
        setEndTime(format(end, 'HH:mm'));
        setStatus(booking.status || 'confirmed');
        setCustomerNotes(booking.customer_notes || '');
        setUseNewContact(false);
        setNewContactPhone('');
        setNewContactName('');
      }
    } else {
      // Creating new booking
      let startDateTime: Date;

      try {
        // Use initialDate and initialHour if provided (from time slot click)
        if (initialDate) {
          startDateTime = new Date(initialDate);
          // Validate the date
          if (isNaN(startDateTime.getTime())) {
            startDateTime = new Date();
          } else {
            if (
              initialHour !== null &&
              initialHour !== undefined &&
              initialHour >= 0 &&
              initialHour < 24
            ) {
              startDateTime.setHours(initialHour, 0, 0, 0);
            }
          }
        } else {
          startDateTime = new Date();
        }
      } catch (error) {
        console.error('Error initializing date:', error);
        startDateTime = new Date();
      }

      const defaultEnd = new Date(startDateTime.getTime() + 60 * 60 * 1000); // 1 hour later
      setServiceId('');
      setContactId('');
      const normalizedStartDate = format(startDateTime, 'yyyy-MM-dd');
      setStartDate(normalizedStartDate);
      setStartTime(format(startDateTime, 'HH:mm'));
      setEndTime(format(defaultEnd, 'HH:mm'));
      setStatus('confirmed');
      setCustomerNotes('');
      setUseNewContact(false);
      setNewContactPhone('');
      setNewContactName('');
    }

    // Mark initial load as complete after state initialization.
    const timer = setTimeout(() => {
      isInitialLoad.current = false;
    }, 0);

    return () => clearTimeout(timer);
  }, [booking, isOpen, initialDate, initialHour, toast, t]);

  // Auto-calculate end time based on selected service duration
  useEffect(() => {
    if (!isOpen) return;

    // Skip auto-calculation during initial load (when editing existing booking)
    if (isInitialLoad.current) return;

    // Only auto-calculate if a service is selected
    if (!serviceId || serviceId === 'none') return;

    // Find the selected service
    const selectedService = services.find(s => s.id.toString() === serviceId);
    if (!selectedService) return;

    // Parse start date and time
    if (!startDate || !startTime) return;

    try {
      const startDateTime = new Date(`${startDate}T${startTime}`);
      if (isNaN(startDateTime.getTime())) return;

      // Calculate end time by adding service duration (same day)
      const endDateTime = new Date(
        startDateTime.getTime() + selectedService.duration_minutes * 60 * 1000
      );

      setEndTime(format(endDateTime, 'HH:mm'));
    } catch (error) {
      console.error('Error calculating end time:', error);
    }
  }, [serviceId, startDate, startTime, services, isOpen]);

  // Reset initial load flag when dialog closes
  useEffect(() => {
    if (!isOpen) {
      isInitialLoad.current = true;
      setShowOutsideHoursConfirm(false);
    }
  }, [isOpen]);

  // Reset confirmation when date/time changes
  useEffect(() => {
    setShowOutsideHoursConfirm(false);
  }, [startDate, startTime, endTime]);

  const handleSave = async () => {
    if (!startDate || !startTime || !endTime) {
      toast({
        variant: 'destructive',
        title: t(
          'agent.calendar.appointment.errors.invalid_time.translation',
          'Invalid Time'
        ),
        description: t(
          'agent.calendar.appointment.errors.time_required.translation',
          'Please provide start date/time and end time.'
        ),
      });
      return;
    }

    // Validate new contact if using new contact option
    if (useNewContact && !newContactPhone.trim()) {
      toast({
        variant: 'destructive',
        title: t(
          'agent.calendar.appointment.errors.phone_required.translation',
          'Phone Number Required'
        ),
        description: t(
          'agent.calendar.appointment.errors.phone_required_description.translation',
          'Please provide a phone number for the new contact.'
        ),
      });
      return;
    }

    if (useNewContact && !isValidPhoneNumber(newContactPhone)) {
      toast({
        variant: 'destructive',
        title: t(
          'agent.calendar.appointment.errors.phone_invalid.translation',
          'Invalid Phone Number'
        ),
        description: t(
          'agent.calendar.appointment.errors.phone_invalid_description.translation',
          'Please provide a valid phone number.'
        ),
      });
      return;
    }

    let startDateTime: Date;
    let endDateTime: Date;

    try {
      startDateTime = new Date(`${startDate}T${startTime}`);
      endDateTime = new Date(`${startDate}T${endTime}`); // Same day as start

      // Validate dates
      if (isNaN(startDateTime.getTime()) || isNaN(endDateTime.getTime())) {
        toast({
          variant: 'destructive',
          title: t(
            'agent.calendar.appointment.errors.invalid_time.translation',
            'Invalid Time'
          ),
          description: t(
            'agent.calendar.appointment.errors.invalid_date_format.translation',
            'Please provide valid date and time values.'
          ),
        });
        return;
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t(
          'agent.calendar.appointment.errors.invalid_time.translation',
          'Invalid Time'
        ),
        description: t(
          'agent.calendar.appointment.errors.invalid_date_format.translation',
          'Please provide valid date and time values.'
        ),
      });
      return;
    }

    if (endDateTime <= startDateTime) {
      toast({
        variant: 'destructive',
        title: t(
          'agent.calendar.appointment.errors.invalid_time.translation',
          'Invalid Time'
        ),
        description: t(
          'agent.calendar.appointment.errors.end_before_start.translation',
          'End time must be after start time.'
        ),
      });
      return;
    }

    const isOutsideHours =
      isDayClosed ||
      (dayOpenTime &&
        dayCloseTime &&
        (startTime < dayOpenTime || endTime > dayCloseTime));

    if (isOutsideHours && !showOutsideHoursConfirm) {
      setShowOutsideHoursConfirm(true);
      return;
    }

    setShowOutsideHoursConfirm(false);

    try {
      setIsSaving(true);

      // Handle new contact creation if needed
      let finalContactId: number | null = null;
      if (useNewContact && newContactPhone.trim()) {
        try {
          const newContact = await contactsApi.createContact(
            agentId,
            newContactPhone.trim(),
            newContactName.trim() || newContactPhone.trim()
          );
          finalContactId = newContact.id;

          // Notify parent component about the new contact
          if (onContactCreated) {
            onContactCreated(newContact);
          }
        } catch (error) {
          toast({
            variant: 'destructive',
            title: t(
              'agent.calendar.appointment.errors.contact_create_failed.translation',
              'Failed to Create Contact'
            ),
            description:
              error instanceof Error
                ? error.message
                : t('common.unknown_error.translation', 'Unknown error'),
          });
          setIsSaving(false);
          return;
        }
      } else if (contactId && contactId !== 'none') {
        finalContactId = parseInt(contactId);
      }

      const bookingData: CreateBookingInput | UpdateBookingInput = {
        agent_id: agentId,
        service_id:
          serviceId && serviceId !== 'none' ? parseInt(serviceId) : null,
        contact_id: finalContactId,
        start_time: startDateTime.toISOString(),
        end_time: endDateTime.toISOString(),
        status,
        customer_notes: customerNotes || null,
      };

      if (booking) {
        // Update existing booking
        await onSave(bookingData);
      } else {
        // Create new booking
        await onSave(bookingData);
      }

      toast({
        title: booking
          ? t(
              'agent.calendar.appointment.updated.translation',
              'Appointment Updated'
            )
          : t(
              'agent.calendar.appointment.created.translation',
              'Appointment Created'
            ),
      });

      onClose();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t(
          'agent.calendar.appointment.errors.save_failed.translation',
          'Save Failed'
        ),
        description:
          error instanceof Error
            ? error.message
            : t('common.unknown_error.translation', 'Unknown error'),
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal open={isOpen} onOpenChange={open => !open && onClose()}>
      <ModalContent className="max-w-2xl lg:max-h-[90vh] lg:overflow-y-auto">
        <ModalHeader>
          <ModalTitle
            description={t(
              'agent.calendar.appointment.dialog.description.translation',
              'Manage appointment details and scheduling.'
            )}
          >
            {booking
              ? t(
                  'agent.calendar.appointment.edit.translation',
                  'Edit Appointment'
                )
              : t(
                  'agent.calendar.appointment.create.translation',
                  'Create Appointment'
                )}
          </ModalTitle>
        </ModalHeader>

        <div className={cn('space-y-4', isMobile ? 'space-y-3 py-2' : 'py-4')}>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>
                {t('agent.calendar.appointment.service.translation', 'Service')}
              </Label>
              <Select
                value={serviceId || undefined}
                onValueChange={value =>
                  setServiceId(value === 'none' ? '' : value)
                }
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={t(
                      'agent.calendar.appointment.service.placeholder.translation',
                      'Select a service'
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    {t(
                      'agent.calendar.appointment.no_service.translation',
                      'No Service'
                    )}
                  </SelectItem>
                  {services.map(service => (
                    <SelectItem key={service.id} value={service.id.toString()}>
                      {service.name}
                      {!service.enabled && (
                        <span className="ml-2 text-xs text-amber-600 dark:text-amber-400">
                          {t(
                            'agent.calendar.appointment.service_disabled_badge.translation',
                            '(Disabled)'
                          )}
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isSelectedServiceDisabled && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  {t(
                    'agent.calendar.appointment.service_disabled_notice.translation',
                    'This service is disabled in business settings.'
                  )}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>
                {t('agent.calendar.appointment.contact.translation', 'Contact')}
              </Label>
              <Select
                value={contactId || undefined}
                onValueChange={value =>
                  setContactId(value === 'none' ? '' : value)
                }
                disabled={useNewContact}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={t(
                      'agent.calendar.appointment.contact.placeholder.translation',
                      'Select a contact'
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    {t(
                      'agent.calendar.appointment.no_contact.translation',
                      'No Contact'
                    )}
                  </SelectItem>
                  {contacts.map(contact => (
                    <SelectItem key={contact.id} value={contact.id.toString()}>
                      {contact.name || contact.caller_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="use-new-contact"
                checked={useNewContact}
                onCheckedChange={checked => {
                  const val = checked === true;
                  setUseNewContact(val);
                  if (val) {
                    setContactId('');
                  } else {
                    setNewContactPhone('');
                    setNewContactName('');
                  }
                }}
              />
              <Label
                htmlFor="use-new-contact"
                className="text-sm font-normal cursor-pointer"
              >
                {t(
                  'agent.calendar.appointment.new_contact.translation',
                  'New Contact'
                )}
              </Label>
            </div>
            {useNewContact && (
              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="tel"
                  value={newContactPhone}
                  onChange={e => setNewContactPhone(e.target.value)}
                  placeholder={t(
                    'agent.calendar.appointment.phone.placeholder.translation',
                    'Phone number (e.g., +1234567890)'
                  )}
                  aria-invalid={hasInvalidNewContactPhone}
                  required
                />
                <Input
                  type="text"
                  value={newContactName}
                  onChange={e => setNewContactName(e.target.value)}
                  placeholder={t(
                    'agent.calendar.appointment.contact_name.placeholder.translation',
                    'Contact name (optional)'
                  )}
                />
              </div>
            )}
            {hasInvalidNewContactPhone && (
              <p className="text-xs text-red-500 dark:text-red-400">
                {t(
                  'agent.calendar.appointment.errors.phone_invalid_description.translation',
                  'Please provide a valid phone number.'
                )}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <div className={cn('gap-4', isMobile ? 'flex flex-col' : 'flex')}>
              <div className="w-full space-y-2">
                <Label>
                  {t(
                    'agent.calendar.appointment.start_date.translation',
                    'Date'
                  )}
                </Label>
                <Popover
                  open={isDatePickerOpen}
                  onOpenChange={setIsDatePickerOpen}
                >
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !startDate && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {selectedStartDate ? (
                        format(selectedStartDate, 'dd/MM/yyyy')
                      ) : (
                        <span>
                          {t(
                            'agent.calendar.appointment.start_date.placeholder.translation',
                            'DD/MM/YYYY'
                          )}
                        </span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedStartDate}
                      onSelect={date => {
                        if (!date) return;
                        setStartDate(format(date, 'yyyy-MM-dd'));
                        setIsDatePickerOpen(false);
                      }}
                      locale={dateLocale.dateFns}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div
                className={cn(
                  'flex items-end gap-2',
                  isMobile ? 'w-full' : 'shrink-0'
                )}
              >
                <div className="min-w-0 flex-1 space-y-2">
                  <Label>
                    {t(
                      'agent.calendar.appointment.start_time.translation',
                      'Start Time'
                    )}
                  </Label>
                  <TimeInput24h
                    value={startTime || '00:00'}
                    onChange={setStartTime}
                    minuteStep={5}
                  />
                </div>
                <span className="shrink-0 pb-2 text-muted-foreground">–</span>
                <div className="min-w-0 flex-1 space-y-2">
                  <Label>
                    {t(
                      'agent.calendar.appointment.end_time.translation',
                      'End Time'
                    )}
                  </Label>
                  <TimeInput24h
                    value={endTime || '00:00'}
                    onChange={setEndTime}
                    minuteStep={5}
                    minTime={startTime}
                  />
                </div>
              </div>
            </div>
            {effectiveHours && (
              <p
                className={`text-xs ${isDayClosed ? 'text-red-500 dark:text-red-400' : 'text-muted-foreground'}`}
              >
                {isDayClosed
                  ? t(
                      'agent.calendar.appointment.day_closed_hint.translation',
                      'Closed on this day'
                    )
                  : t(
                      'agent.calendar.appointment.business_hours_hint.translation',
                      {
                        open: dayOpenTime,
                        close: dayCloseTime,
                        defaultValue: 'Business hours: {{open}} – {{close}}',
                      }
                    )}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>
              {t(
                'agent.calendar.appointment.customer_notes.translation',
                'Customer Notes'
              )}
            </Label>
            <Textarea
              value={customerNotes}
              onChange={e => setCustomerNotes(e.target.value)}
              placeholder={t(
                'agent.calendar.appointment.customer_notes.placeholder.translation',
                'Optional notes about this appointment...'
              )}
              rows={isMobile ? 3 : 4}
            />
          </div>
        </div>

        {showOutsideHoursConfirm && (
          <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/50 p-3">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
              {isDayClosed
                ? t(
                    'agent.calendar.appointment.confirm_closed.translation',
                    'The business is closed on this day. Are you sure you want to schedule this appointment?'
                  )
                : t(
                    'agent.calendar.appointment.confirm_outside_hours.translation',
                    {
                      open: dayOpenTime,
                      close: dayCloseTime,
                      defaultValue:
                        'This appointment falls outside business hours ({{open}} – {{close}}). Are you sure you want to proceed?',
                    }
                  )}
            </p>
          </div>
        )}

        <ModalFooter className={isMobile ? 'pt-2' : undefined}>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            {t('common.cancel.translation', 'Cancel')}
          </Button>
          {showOutsideHoursConfirm ? (
            <>
              <Button
                variant="outline"
                onClick={() => setShowOutsideHoursConfirm(false)}
                disabled={isSaving}
              >
                {t('common.go_back.translation', 'Go Back')}
              </Button>
              <Button variant="brand" onClick={handleSave} disabled={isSaving}>
                {isSaving
                  ? t('common.saving.translation', 'Saving...')
                  : t(
                      'agent.calendar.appointment.confirm_proceed.translation',
                      'Schedule Anyway'
                    )}
              </Button>
            </>
          ) : (
            <Button variant="brand" onClick={handleSave} disabled={isSaving}>
              {isSaving
                ? t('common.saving.translation', 'Saving...')
                : t('common.save.translation', 'Save')}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
